import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import type { ActionTrackingContext } from '../../actions/framework/action-tracking';
import { ChatModel } from '../chat.model';
import {
  getCocoConversationId,
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import { ok } from 'neverthrow';

export class ReplayAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  title = 'Replay';
  inputSchema = z.object({
    messageId: z.string(),
  });
  group = 'Chat';

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(ChatModel);
  }

  async execute(
    params: { messageId: string },
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const chatModel = context.targetedModelOfType(ChatModel)!;
    chatModel.chatController.setMessages((currentMessages) => {
      const updatedMessages = [...currentMessages];
      const messageIndex = updatedMessages.findIndex(
        (msg) => msg.id === params.messageId
      );
      return updatedMessages.slice(0, messageIndex + 1);
    });

    await chatModel.chatController.regenerate();

    return ok({});
  }

  track({
    context,
  }: ActionTrackingContext<
    { messageId: string },
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const conversationId = getCocoConversationId(context);
    const chatModel = context.targetedModelOfType(ChatModel);
    if (conversationId === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.MessageReplayed, {
      conversationId,
      surface: getChatSurface(context),
      model: chatModel?.model ?? null,
    });
  }
}
